import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ImagePicker } from "@/components/images/image-picker";
import { ImageCategory } from "@prisma/client";
import Image from "next/image";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  accountId?: string;
  title?: string;
  businessUnit?: string;
  image?: string;
}

interface AddContactFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (contact: Omit<Contact, 'id'>) => void;
  accountId?: string; // Optional now
  accounts?: { id: string; name: string }[]; // List of available accounts
  hideOptionalFields?: boolean; // Hide phone, businessUnit, and image fields
  initialEmail?: string; // Pre-fill email when opening (e.g. from share modal search)
  /** Room/share flows: never collect relationship intelligence here. */
  inviteContext?: 'room' | 'account';
}

export function AddContactForm({
  isOpen,
  onClose,
  onSubmit,
  accountId,
  accounts = [],
  hideOptionalFields = false,
  initialEmail,
  inviteContext = 'account',
}: AddContactFormProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState(accountId || '');
  const [phone, setPhone] = useState('');
  const [title, setTitle] = useState('');
  const [businessUnit, setBusinessUnit] = useState('');
  const [image, setImage] = useState('');
  const [isImagePickerOpen, setIsImagePickerOpen] = useState(false);

  // Debug logging
  useEffect(() => {
    console.log('[AddContactForm] Component rendered/re-rendered');
    console.log('[AddContactForm] Props received - isOpen:', isOpen, 'accountId:', accountId);
    if (accountId) setSelectedAccountId(accountId);
  }, [isOpen, accountId]);

  useEffect(() => {
    console.log('[AddContactForm] isOpen prop changed to:', isOpen);
  }, [isOpen]);

  // Pre-fill email when opening with initialEmail (e.g. from share modal)
  useEffect(() => {
    if (isOpen && initialEmail !== undefined && initialEmail.trim() !== '') {
      setEmail(initialEmail.trim());
    }
  }, [isOpen, initialEmail]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('[AddContactForm] Form submitted with data:', { firstName, lastName, email, phone, accountId: selectedAccountId, title, businessUnit, image });
    onSubmit({ firstName, lastName, email, phone, accountId: selectedAccountId, title, businessUnit, image });
    onClose();
    // Reset form fields
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setTitle('');
    setBusinessUnit('');
    setImage('');
    if (!accountId) setSelectedAccountId('');
  };

  const handleOpenChange = (open: boolean) => {
    console.log('[AddContactForm] Dialog onOpenChange called with:', open);
    if (!open) {
      console.log('[AddContactForm] Dialog is closing, calling onClose');
      onClose();
    } else {
      console.log('[AddContactForm] Dialog is opening');
    }
  };

  console.log('[AddContactForm] Rendering Dialog with open=', isOpen);

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Add New Contact</DialogTitle>
          {inviteContext === 'room' ? (
            <p className="text-sm text-muted-foreground pt-1">
              Name and email only. Stakeholder roles and internal coverage are managed on the
              account Relationship Map by your team.
            </p>
          ) : accountId ? (
            <p className="text-sm text-muted-foreground pt-1">
              Add basic details here. Open the Relationship Map tab to assign buying-committee
              roles, hierarchy, and internal owners.
            </p>
          ) : null}
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="firstName" className="text-right">
                First Name
              </Label>
              <Input
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="col-span-3"
                required
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="lastName" className="text-right">
                Last Name
              </Label>
              <Input
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="col-span-3"
                required
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="email" className="text-right">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="col-span-3"
                required
              />
            </div>
            {!hideOptionalFields && (
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="phone" className="text-right">
                  Phone
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="col-span-3"
                />
              </div>
            )}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="title" className="text-right">
                Title
              </Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="col-span-3"
              />
            </div>
            {!accountId && accounts.length > 0 && (
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="account" className="text-right">
                  Account
                </Label>
                <div className="col-span-3">
                  <Select
                    value={selectedAccountId === 'no_account' ? '' : selectedAccountId}
                    onValueChange={(val) => setSelectedAccountId(val === 'no_account' ? '' : val)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select an account (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no_account">No Account</SelectItem>
                      {accounts.map((account) => (
                        <SelectItem key={account.id} value={account.id}>
                          {account.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            {!hideOptionalFields && (
              <>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="businessUnit" className="text-right">
                    Business Unit
                  </Label>
                  <Input
                    id="businessUnit"
                    value={businessUnit}
                    onChange={(e) => setBusinessUnit(e.target.value)}
                    className="col-span-3"
                  />
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label className="text-right">
                    Image
                  </Label>
                  <div className="col-span-3 flex items-center gap-4">
                    <div className="relative h-12 w-12 overflow-hidden rounded-full border bg-muted">
                      {image ? (
                        <Image
                          src={image.startsWith("http") ? `/api/content-library/proxy-image?url=${encodeURIComponent(image)}` : image}
                          alt="Contact Image"
                          fill
                          className="object-cover"
                          unoptimized
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-muted-foreground text-xs">
                          No Img
                        </div>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setIsImagePickerOpen(true)}
                    >
                      Choose Image
                    </Button>
                    <ImagePicker
                      open={isImagePickerOpen}
                      onOpenChange={setIsImagePickerOpen}
                      onSelect={(img) => setImage(img.url)}
                      category={ImageCategory.AVATAR}
                      cropCircular={true}
                      cropAspect={1}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button type="submit">Add Contact</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
